class DataCache {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
  }

  // Set data with TTL
  set(key, data, ttl = this.defaultTTL) {
    // Clear existing timer if exists
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    // Store data with timestamp
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });

    // Set expiration timer
    const timer = setTimeout(() => {
      this.delete(key);
    }, ttl);

    this.timers.set(key, timer);

  }

  // Get data if not expired
  get(key) {
    const item = this.cache.get(key);

    if (!item) {
      // console.log(`📦 Cache MISS: ${key}`);
      return null;
    }

    const isExpired = Date.now() - item.timestamp > item.ttl;

    if (isExpired) {
      // console.log(`📦 Cache EXPIRED: ${key}`);
      this.delete(key);
      return null;
    }

    // console.log(`📦 Cache HIT: ${key}`);
    return item.data;
  }

  // Delete specific key
  delete(key) {
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }

    const deleted = this.cache.delete(key);
    if (deleted) {
      console.log(`📦 Cache DELETE: ${key}`);
    }
    return deleted;
  }

  // Check if key exists and is valid
  has(key) {
    return this.get(key) !== null;
  }

  // Clear all cache with pattern matching
  invalidate(pattern) {
    const regex = new RegExp(pattern.replace("*", ".*"));
    const keysToDelete = [];

    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.delete(key));
    console.log(
      `📦 Cache INVALIDATE: ${pattern} (${keysToDelete.length} keys)`
    );
  }

  // Clear all cache
  clear() {
    // Clear all timers
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }

    this.cache.clear();
    this.timers.clear();
    console.log("📦 Cache CLEARED");
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      memory: this._estimateMemoryUsage(),
    };
  }

  // Estimate memory usage (rough calculation)
  _estimateMemoryUsage() {
    let totalSize = 0;
    for (const [key, value] of this.cache) {
      totalSize += JSON.stringify({ key, value }).length;
    }
    return `${(totalSize / 1024).toFixed(2)} KB`;
  }

  // Helper methods for specific data types

  // User profile cache helpers
  setUserProfile(uid, profileData) {
    this.set(`user-profile-${uid}`, profileData, 5 * 60 * 1000); // 5 minutes
  }

  getUserProfile(uid) {
    return this.get(`user-profile-${uid}`);
  }

  // Quiz data cache helpers
  setQuizData(quizId, quizData) {
    this.set(`quiz-data-${quizId}`, quizData, 10 * 60 * 1000); // 10 minutes
  }

  getQuizData(quizId) {
    return this.get(`quiz-data-${quizId}`);
  }

  // Shared quiz cache helpers
  setSharedQuiz(sharedId, sharedData) {
    this.set(`shared-quiz-${sharedId}`, sharedData, 10 * 60 * 1000); // 10 minutes
  }

  getSharedQuiz(sharedId) {
    return this.get(`shared-quiz-${sharedId}`);
  }

  // Quiz completion status cache
  setQuizCompletion(uid, completionData) {
    this.set(`quiz-completion-${uid}`, completionData, 3 * 60 * 1000); // 3 minutes (shorter TTL)
  }

  getQuizCompletion(uid) {
    return this.get(`quiz-completion-${uid}`);
  }
}

// Create singleton instance
const dataCache = new DataCache();

export default dataCache;
